/** Fills in the signed-in user's own photo on a person row that came back without one. */
import { useCallback } from "react";
import { readCurrentMembershipId, readCurrentUserId, useCurrentUser } from "./useCurrentUser";

interface PersonWithAvatar {
  id?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
}

const sameEmail = (a?: string | null, b?: string | null): boolean =>
  Boolean(a) && Boolean(b) && a!.trim().toLowerCase() === b!.trim().toLowerCase();

/** Returns a function that gives the signed-in user's row their own photo when the API sent none. */
export function useOwnAvatarFallback(): <T extends PersonWithAvatar>(person: T) => T {
  const { email, avatarUrl } = useCurrentUser();
  return useCallback(
    (person) => {
      if (person.avatarUrl || !avatarUrl) return person;
      // People come back keyed by account id or by tenant membership id, depending on the source.
      const myIds = [readCurrentUserId(), readCurrentMembershipId()].filter(Boolean);
      const isMe = (Boolean(person.id) && myIds.includes(person.id!)) || sameEmail(person.email, email);
      return isMe ? { ...person, avatarUrl } : person;
    },
    [email, avatarUrl],
  );
}
